// src/features/explore/ExploreCard.tsx — explore feed card (links to the post).
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { formatCount } from '@/components/formatCount';
import { ExploreCoverThumb } from './ExploreCoverThumb';
import { postPath } from '@/core/lib/postUrl';
import type { ExploreItem } from '@/core/models/explore';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  text-decoration: none;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  transition: transform 120ms ease, border-color 120ms ease;

`;

const Thumb = styled.div`
  height: 120px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Author = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Counts = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 2px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Stat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export interface ExploreCardProps {
  item: ExploreItem;
}

export function ExploreCard({ item }: ExploreCardProps) {
  return (
    // 1.24.00 D1.1: an explore item carries the legacy id only (no wire change
    // here), so the detail page relabels the address bar on arrival — D1.2.
    <Card to={postPath({ id: item.postId })} aria-label={item.title}>
      <Thumb>
        <ExploreCoverThumb postId={item.postId} cover={item.cover} />
      </Thumb>
      <Body>
        <Title>{item.title}</Title>
        <Author>
          {item.author} {item.handle}
        </Author>
        <Counts>
          <Stat aria-label={`${item.comments} comments`}><Icon name="comment" size={14} /> {formatCount(item.comments)}</Stat>
          <Stat aria-label={`${item.cheers} cherishes`}><Icon name="cherish" size={14} /> {formatCount(item.cheers)}</Stat>
        </Counts>
      </Body>
    </Card>
  );
}
